import {noop} from '@benchling/js-utils';
import {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {
  STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';
import TableUtil from 'notebook/tables/TableUtil';
import {BenchlingState, useAppSelector} from 'redux-stores/reduceApp';

import {CollapsedTableState, selectTableCollapsed} from './reduceCollapsedTablesUI';

export function isTableCollapsed({
  collapseId,
  collapsedStateById,
  rowCount,
  cellCount,
  tableKind,
  autoCollapseLimit,
}: {
  collapseId: string;
  collapsedStateById: CollapsedTableState;
  rowCount: number;
  cellCount: number;
  tableKind: StructuredTableKind | null;
  autoCollapseLimit?: number;
}): boolean {
  return (
    collapsedStateById[collapseId] ??
    shouldCollapseTableInitially(rowCount, cellCount, tableKind, autoCollapseLimit)
  );
}

export function createInputTableCollapseId(stageEntryId: string): string {
  return `input-${stageEntryId}`;
}

export function createOutputTableCollapseId(stageEntryId: string): string {
  return `output-${stageEntryId}`;
}
export function getCollapseId({collapseId, cid}: {collapseId?: string; cid: string}): string {
  return collapseId || cid;
}

export function getIsOverInlineRowLimit(numRows: number, tableKind: StructuredTableKind | null): boolean {
  const rowLimit = BenchlingConfig.ELN_INLINE_TABLE_VIEW_ROW_LIMIT;
  return (
    !STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE.includes(tableKind) &&
    numRows > 0 &&
    Number.isInteger(rowLimit) &&
    rowLimit > -1 &&
    numRows > rowLimit
  );
}

export function getIsOverInlineCellLimit(numCells: number, tableKind: StructuredTableKind | null): boolean {
  const cellLimit = BenchlingConfig.ELN_INLINE_TABLE_VIEW_CELL_LIMIT;
  return (
    !STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE.includes(tableKind) &&
    numCells > 0 &&
    Number.isInteger(cellLimit) &&
    cellLimit > -1 &&
    numCells > cellLimit
  );
}

export function shouldCollapseTableInitially(
  numRows: number,
  numCells: number,
  tableKind: StructuredTableKind | null,
  autoCollapseLimit?: number
): boolean {
  return (
    (!STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE.includes(tableKind) &&
      numRows > (autoCollapseLimit || BenchlingConfig.ELN_ROW_LIMIT_FOR_AUTO_COLLAPSE)) ||
    getIsOverInlineRowLimit(numRows, tableKind) ||
    getIsOverInlineCellLimit(numCells, tableKind)
  );
}

function getIsPermaCollapsed({
  isOverInlineRowLimit,
  isOverInlineCellLimit,
  isEditViewAvailable,
  isReadonlyViewer,
  isSupportedByCsvExport,
}: {
  isOverInlineRowLimit: boolean;
  isOverInlineCellLimit: boolean;
  isEditViewAvailable: boolean;
  isReadonlyViewer: boolean;
  isSupportedByCsvExport: boolean;
}): boolean {
  if (isReadonlyViewer && !isSupportedByCsvExport) {
    return false;
  }
  if (isReadonlyViewer && (isOverInlineRowLimit || isOverInlineCellLimit)) {
    return true;
  }
  return false;
}

export function useTableCollapse({
  autoCollapseLimit,
  collapseId,
  cid,
  forceCollapsed,
  isFocused,
  isMaximized,
  isSplitScreen,
  isCollapsible,
  viewer,
  table,
  maximumHeight,
  setMaximizedTable,
  setSplitScreenTable,
}: Pick<
  TableEditableProps,
  | 'autoCollapseLimit'
  | 'collapseId'
  | 'cid'
  | 'forceCollapsed'
  | 'isFocused'
  | 'isMaximized'
  | 'isSplitScreen'
  | 'isCollapsible'
  | 'viewer'
  | 'table'
  | 'maximumHeight'
  | 'setMaximizedTable'
  | 'setSplitScreenTable'
>): {
  isCollapsed: boolean;
  isCollapsedInitially: boolean;
  isPermaCollapsed: boolean;
} {
  const id = getCollapseId({collapseId, cid});
  const tableKind = TableUtil.getTableKind(table);
  const isLegacyTable = TableUtil.isLegacyTable(table._tableType);
  const isReadonlyViewer = Boolean(viewer);
  const {rowCount, cellCount} = TableUtil.getTableDimensionsFromTable(table);

  const isCollapsedInitially =
    Boolean(isCollapsible) && shouldCollapseTableInitially(rowCount, cellCount, tableKind, autoCollapseLimit);

  const isOverInlineRowLimit = getIsOverInlineRowLimit(rowCount, tableKind);
  const isOverInlineCellLimit = getIsOverInlineCellLimit(cellCount, tableKind);

  const isEditViewAvailable =
    (setMaximizedTable != null && setMaximizedTable !== noop) ||
    (setSplitScreenTable != null && setSplitScreenTable !== noop);

  const isPermaCollapsed = getIsPermaCollapsed({
    isOverInlineRowLimit,
    isOverInlineCellLimit,
    isEditViewAvailable,
    isReadonlyViewer,
    isSupportedByCsvExport: !isLegacyTable,
  });

  const isCollapsedSelector = useCallback((state: BenchlingState) => selectTableCollapsed(state, id), [id]);

  const isCollapsed = useAppSelector(isCollapsedSelector);

  const getIsCollapsed = (): boolean => {
    if (forceCollapsed || isFocused) {
      return true;
    }

    if (isMaximized || isSplitScreen) {
      return false;
    }

    if (!isCollapsible) {
      return false;
    }

    if (isPermaCollapsed) {
      return true;
    }

    // Print and export view should render non-permacollapsed tables as expanded
    if (viewer) {
      return false;
    }

    return isCollapsed ?? isCollapsedInitially;
  };

  return {
    isCollapsed: getIsCollapsed(),
    isCollapsedInitially,
    isPermaCollapsed,
  };
}
